import { useHttp } from '@/composables/useHttp'
import type { Distribution } from '@/enums/Distribution'
import type { PaymentStatus } from '@/enums/PaymentStatus'
import type { OrderReceipt } from '@/types/OrderReceipt'
import type { OrderStatus } from '@/types/OrderStatus'

type ShowOrder = {
  type: 'order',
  id: string,
  attributes: {
    hash: string,
    instructions: string | null,
    stand_id: number | null,
    created_at: string,
    is_asap: boolean,
    is_confirmed: boolean,
    is_auto_acknowledged: boolean,
    is_expired: boolean,
    distribution_at: string,
    distribution: Distribution,
    customer: {
      id: string | number,
      name: string,
      phone_number: string,
      email: string,
    },
    location: {
      latitude: number,
      longitude: number,
      lat: number,
      lng: number,
      street: string,
      house_number: string | number,
      zipcode: string,
      city: string,
    },
    timeslot: { start: string, end: string } | null,
    payment_status: PaymentStatus,
    receipt: OrderReceipt,
    status: OrderStatus,
    stamps_gained: number,
  },
  relationships: {
    establishment: {
      type: string,
      id: string | number,
      attributes: {
        title: string,
        location: {
          street: string,
          zipcode: string,
          city: {
            id: string | number,
            slug: string,
            name: string,
            country: {
              id: string | number,
              code: string,
            }
          },
          lat: number,
          lng: number,
        }
      }
    }
  }
}

export function useOrderHttp() {
  const http = useHttp()

  async function createOrder(order: any) {
    return (await http.post('/orders', order)).data.data
  }

  async function findByUuid(uuid: string) {
    return (await http.get<{ data: ShowOrder }>(`/orders/${uuid}`)).data.data
  }

  async function restore(order: string) {
    return (await http.post(`/orders/${order}/restore`, order)).data.data
  }

  async function fakePayment(order: string, method: string, callbackUrl: string, option: string | null) {
    return (await http.post(`/orders/${order}/payment/integrations/fake`, {
      method,
      callback_url: callbackUrl,
      ...(option !== null && {option}),
    })).data.data.redirect_url as string
  }

  async function molliePayment(order: string, method: string, callbackUrl: string, option: string | null) {
    return (await http.post(`/orders/${order}/payment/integrations/mollie`, {
      method,
      callback_url: callbackUrl,
      ...(option !== null && {option}),
    })).data.data.redirect_url as string
  }

  async function findByReviewToken(token: string) {
    const response = await http.get(`/orders/by-review-token/${token}`)

    return response.data.data
  }

  async function createStripePayment(orderId: string) {
    const response = await http.post(`orders/${orderId}/payment/integrations/stripe`)

    return response.data
  }

  return {
    findByUuid,
    findByReviewToken,
    restore,
    fakePayment,
    molliePayment,
    createOrder,
    createStripePayment,
  }
}
